import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { relTime, formatCurrency } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Plus, Trash2, Edit3, Save, X, Phone, Mail, Building2, LayoutGrid, List as ListIcon, Tag, Users, ListPlus, ListMinus, CheckSquare, FileText, Download, Send, Link2 } from "lucide-react";
import ChannelIcon, { CHANNEL_COLORS } from "../components/ChannelIcon";
import ContactAvatar from "../components/ContactAvatar";
import { useAuth } from "@/lib/auth";
import { API_BASE } from "@/lib/api";

const ALL_CHANNELS = ["all", "chat", "email", "whatsapp", "facebook", "instagram", "manual"];

export default function Contacts() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "company_admin" || user?.role === "division_manager";

  const [search, setSearch] = useState("");
  const [channel, setChannel] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [activeListId, setActiveListId] = useState(null);
  const [view, setView] = useState("cards");  // cards | table
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showNewList, setShowNewList] = useState(false);
  const [selected, setSelected] = useState(new Set());  // Set of contact ids
  const [selectMode, setSelectMode] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (openId) setSelectedId(openId);
  }, []);

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["contacts", search, channel, tagFilter, activeListId],
    queryFn: () => api.get("/contacts", { params: {
      q: search || undefined,
      channel: channel !== "all" ? channel : undefined,
      tag: tagFilter || undefined,
      list_id: activeListId || undefined,
    }}).then((r) => r.data),
  });

  const { data: lists = [] } = useQuery({
    queryKey: ["contact-lists"],
    queryFn: () => api.get("/contact-lists").then((r) => r.data),
  });

  const tagCloud = useMemo(() => {
    const map = new Map();
    contacts.forEach((c) => (c.tags || []).forEach((t) => map.set(t, (map.get(t) || 0) + 1)));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [contacts]);

  const toggleSelect = (id) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const selectAll = () => setSelected(new Set(contacts.map((c) => c.id)));
  const clearSelection = () => setSelected(new Set());
  const enterSelectMode = () => setSelectMode(true);
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };

  const bulkMut = useMutation({
    mutationFn: (body) => api.post("/contacts/bulk", body),
    onSuccess: (r) => {
      toast.success(`Applied to ${r.data.processed} contact${r.data.processed === 1 ? "" : "s"}${r.data.skipped?.length ? ` — ${r.data.skipped.length} skipped (linked records)` : ""}`);
      qc.invalidateQueries({ queryKey: ["contacts"] });
      qc.invalidateQueries({ queryKey: ["contact-lists"] });
      clearSelection();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Bulk action failed"),
  });

  return (
    <div>
      <PageHeader
        overline="Directory"
        title="Contacts"
        subtitle="Every person that's ever messaged, been a lead, or been part of a deal — in one place."
        actions={
          <div className="flex items-center gap-2">
            <div className="rounded-sm bg-secondary/40 border border-border p-0.5 flex" data-testid="contacts-view-toggle">
              <button onClick={() => setView("cards")} data-testid="view-cards" className={`px-2.5 py-1.5 rounded-sm text-xs transition-colors ${view === "cards" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-white"}`}><LayoutGrid className="h-3.5 w-3.5" /></button>
              <button onClick={() => setView("table")} data-testid="view-table" className={`px-2.5 py-1.5 rounded-sm text-xs transition-colors ${view === "table" ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-white"}`}><ListIcon className="h-3.5 w-3.5" /></button>
            </div>
            {!selectMode ? (
              <Button data-testid="contacts-select-btn" variant="outline" className="rounded-sm" onClick={enterSelectMode}><CheckSquare className="h-4 w-4 mr-2" />Select</Button>
            ) : (
              <Button data-testid="contacts-exit-select-btn" variant="outline" className="rounded-sm" onClick={exitSelectMode}><X className="h-4 w-4 mr-2" />Cancel</Button>
            )}
            <Button data-testid="contacts-new-btn" className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4 mr-2" />New contact
            </Button>
          </div>
        }
      />

      <div className="px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Lists sidebar */}
        <aside className="lg:col-span-3 space-y-4">
          <Card className="rounded-sm bg-card border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Lists</div>
              <button className="text-xs text-primary hover:underline" onClick={() => setShowNewList(true)} data-testid="contacts-new-list-btn">+ New</button>
            </div>
            <div className="space-y-1">
              <ListItem active={!activeListId} onClick={() => setActiveListId(null)} label="All contacts" count={contacts.length} />
              {lists.map((L) => (
                <ListItem
                  key={L.id}
                  active={activeListId === L.id}
                  onClick={() => setActiveListId(L.id)}
                  label={L.name}
                  count={L.contact_count}
                  color={L.color}
                  onDelete={isAdmin ? () => { if (window.confirm(`Delete list "${L.name}"? Contacts remain, only the list is removed.`)) api.delete(`/contact-lists/${L.id}`).then(() => { toast.success("List deleted"); qc.invalidateQueries({ queryKey: ["contact-lists"] }); if (activeListId === L.id) setActiveListId(null); }); } : null}
                />
              ))}
            </div>
          </Card>
        </aside>

        {/* Main */}
        <div className="lg:col-span-9 space-y-4">
          <Card className="rounded-sm bg-card border-border p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="contacts-search" placeholder="Search name, email, phone, or company…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm bg-secondary/40" />
            </div>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="contacts-channel-filter" className="rounded-sm bg-secondary/40 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALL_CHANNELS.map((c) => <SelectItem key={c} value={c}><span className="capitalize">{c === "all" ? "All channels" : c}</span></SelectItem>)}
              </SelectContent>
            </Select>
            {tagFilter && (
              <Badge className="rounded-sm bg-primary/20 text-primary border-primary/30 cursor-pointer" onClick={() => setTagFilter("")}>
                tag:{tagFilter} <X className="h-3 w-3 ml-1" />
              </Badge>
            )}
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground ml-auto">
              {contacts.length} {contacts.length === 1 ? "contact" : "contacts"}
            </div>
          </Card>

          {tagCloud.length > 0 && !activeListId && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Popular tags:</span>
              {tagCloud.map(([t, count]) => (
                <button key={t} onClick={() => setTagFilter(t === tagFilter ? "" : t)} data-testid={`contacts-tag-${t}`} className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${tagFilter === t ? "bg-primary/20 border-primary/50 text-primary" : "bg-secondary/40 border-border text-muted-foreground hover:text-white hover:border-primary/40"}`}>
                  {t} <span className="opacity-60">·{count}</span>
                </button>
              ))}
            </div>
          )}

          {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && contacts.length === 0 && (
            <Card className="rounded-sm bg-card border-border p-12 text-center">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Empty</div>
              <div className="font-display text-2xl font-extrabold mt-2">No contacts match your filters.</div>
              <div className="text-sm text-muted-foreground mt-2">Try clearing the search, or add a new contact.</div>
            </Card>
          )}

          {view === "cards" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {contacts.map((c) => (
                <ContactCard key={c.id} c={c} onOpen={() => setSelectedId(c.id)} selectMode={selectMode} selected={selected.has(c.id)} onToggle={() => toggleSelect(c.id)} />
              ))}
            </div>
          ) : (
            <ContactTable contacts={contacts} onOpen={setSelectedId} selectMode={selectMode} selected={selected} onToggle={toggleSelect} onToggleAll={(all) => all ? clearSelection() : selectAll()} />
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          contactIds={Array.from(selected)}
          lists={lists}
          isAdmin={isAdmin}
          onAction={(body) => bulkMut.mutate(body)}
          onClose={clearSelection}
        />
      )}

      {selectedId && <ContactDetailDialog contactId={selectedId} lists={lists} onClose={() => setSelectedId(null)} isAdmin={isAdmin} />}
      {showNew && <NewContactDialog onClose={() => setShowNew(false)} onCreated={(id) => { qc.invalidateQueries({ queryKey: ["contacts"] }); setShowNew(false); setSelectedId(id); }} />}
      {showNewList && <NewListDialog onClose={() => setShowNewList(false)} onCreated={() => qc.invalidateQueries({ queryKey: ["contact-lists"] })} />}
    </div>
  );
}

function ListItem({ active, onClick, label, count, color, onDelete }) {
  return (
    <div className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-xs group transition-colors ${active ? "bg-primary/15 text-white" : "text-muted-foreground hover:bg-secondary/50 hover:text-white"}`}>
      <button onClick={onClick} data-testid={`contacts-list-item-${label}`} className="flex-1 flex items-center gap-2 text-left">
        {color && <span className="h-2 w-2 rounded-full flex-none" style={{ backgroundColor: color }} />}
        <span className="flex-1 truncate">{label}</span>
        <span className="text-[10px] opacity-60">{count ?? ""}</span>
      </button>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-muted-foreground/60 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all" data-testid={`contacts-list-delete-${label}`}>
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function ContactCard({ c, onOpen, selectMode, selected, onToggle }) {
  const brand = CHANNEL_COLORS[c.source_channel] || CHANNEL_COLORS.chat;
  return (
    <div
      onClick={() => (selectMode ? onToggle() : onOpen())}
      data-testid="contacts-card"
      className={`text-left bg-card border rounded-sm p-4 hover:border-primary/60 transition-all card-in relative overflow-hidden group cursor-pointer ${selected ? "border-primary" : "border-border"}`}
    >
      <div className="absolute top-0 left-0 h-full w-0.5" style={{ backgroundColor: brand.color, opacity: 0.7 }} />
      <div className="flex items-start gap-3 pl-1">
        {selectMode && (
          <Checkbox checked={selected} onCheckedChange={onToggle} onClick={(e) => e.stopPropagation()} data-testid={`contacts-checkbox-${c.id}`} className="mt-1 flex-none" />
        )}
        <div className="relative flex-none">
          <ContactAvatar src={c.avatar_url} name={c.name} size={44} />
          <span className="absolute -bottom-1 -right-1"><ChannelIcon channel={c.source_channel} size={10} badge /></span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{c.name}</div>
          <div className="text-xs text-muted-foreground truncate flex items-center gap-1">{c.email && <><Mail className="h-3 w-3" />{c.email}</>}</div>
          <div className="text-xs text-muted-foreground truncate flex items-center gap-1">{(c.phone || c.whatsapp_phone) && <><Phone className="h-3 w-3" />{c.phone || c.whatsapp_phone}</>}</div>
          {c.company_name && <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5"><Building2 className="h-3 w-3" />{c.company_name}</div>}
        </div>
      </div>
      {(c.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3 pl-1">
          {(c.tags || []).slice(0, 4).map((t) => <Badge key={t} variant="secondary" className="rounded-sm text-[10px] px-1.5 py-0">{t}</Badge>)}
        </div>
      )}
    </div>
  );
}

function ContactTable({ contacts, onOpen, selectMode, selected, onToggle, onToggleAll }) {
  const allSelected = contacts.length > 0 && selected.size === contacts.length;
  return (
    <Card className="rounded-sm bg-card border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {selectMode && (
                <th className="p-3 w-10"><Checkbox checked={allSelected} onCheckedChange={() => onToggleAll(allSelected)} data-testid="contacts-table-select-all" /></th>
              )}
              <th className="p-3">Name</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Company</th>
              <th className="p-3">Source</th>
              <th className="p-3">Tags</th>
              <th className="p-3">Added</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const brand = CHANNEL_COLORS[c.source_channel] || CHANNEL_COLORS.chat;
              return (
                <tr
                  key={c.id}
                  data-testid="contacts-row"
                  onClick={() => (selectMode ? onToggle(c.id) : onOpen(c.id))}
                  className={`border-b border-border/60 last:border-0 hover:bg-secondary/30 transition-colors cursor-pointer ${selected.has(c.id) ? "bg-primary/5" : ""}`}
                >
                  {selectMode && (
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(c.id)} onCheckedChange={() => onToggle(c.id)} data-testid={`contacts-row-checkbox-${c.id}`} />
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-none">
                        <ContactAvatar src={c.avatar_url} name={c.name} size={28} />
                        <span className="absolute -bottom-1 -right-1"><ChannelIcon channel={c.source_channel} size={8} badge /></span>
                      </div>
                      <div className="font-medium truncate max-w-[220px]">{c.name}</div>
                    </div>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    <div className="truncate max-w-[180px]">{c.email || <span className="opacity-50">—</span>}</div>
                    <div className="truncate max-w-[180px]">{c.phone || c.whatsapp_phone || <span className="opacity-50">—</span>}</div>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground truncate max-w-[160px]">{c.company_name || <span className="opacity-50">—</span>}</td>
                  <td className="p-3">
                    <div className="text-xs capitalize inline-flex items-center gap-1.5" style={{ color: brand.color }}>
                      <ChannelIcon channel={c.source_channel} size={10} />
                      {c.source_channel}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                      {(c.tags || []).slice(0, 2).map((t) => <Badge key={t} variant="secondary" className="rounded-sm text-[10px] px-1.5 py-0">{t}</Badge>)}
                      {(c.tags || []).length > 2 && <span className="text-[10px] text-muted-foreground">+{c.tags.length - 2}</span>}
                    </div>
                  </td>
                  <td className="p-3 text-[10px] text-muted-foreground">{relTime(c.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function BulkActionBar({ count, contactIds, lists, isAdmin, onAction, onClose }) {
  const [tagText, setTagText] = useState("");
  const [showTag, setShowTag] = useState(false);
  const [showList, setShowList] = useState(false);
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-card border border-primary/40 rounded-sm shadow-xl p-3 flex items-center gap-3 max-w-[95vw]" data-testid="contacts-bulk-bar">
      <div className="text-sm font-semibold flex items-center gap-2">
        <CheckSquare className="h-4 w-4 text-primary" />
        {count} selected
      </div>
      <div className="h-6 w-px bg-border" />
      {!showTag && !showList ? (
        <>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setShowTag(true)} data-testid="bulk-add-tag-btn"><Tag className="h-3.5 w-3.5 mr-1" />Add tag</Button>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setShowList(true)} data-testid="bulk-add-to-list-btn"><ListPlus className="h-3.5 w-3.5 mr-1" />Add to list</Button>
          {isAdmin && (
            <Button size="sm" variant="outline" className="rounded-sm border-red-500/30 text-red-400 hover:text-red-300" data-testid="bulk-delete-btn" onClick={() => { if (window.confirm(`Delete ${count} contact(s)? Contacts with linked records will be skipped.`)) onAction({ contact_ids: contactIds, action: "delete" }); }}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
          )}
          <Button size="sm" variant="ghost" className="rounded-sm ml-2" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
        </>
      ) : showTag ? (
        <div className="flex items-center gap-2">
          <Input placeholder="Tag name…" value={tagText} onChange={(e) => setTagText(e.target.value)} className="rounded-sm bg-secondary/40 h-8 w-40" data-testid="bulk-tag-input" autoFocus />
          <Button size="sm" className="rounded-sm bg-primary" onClick={() => { if (tagText.trim()) { onAction({ contact_ids: contactIds, action: "add_tag", tag: tagText.trim() }); setTagText(""); setShowTag(false); } }} data-testid="bulk-tag-apply">Apply</Button>
          <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setShowTag(false)}><X className="h-3.5 w-3.5" /></Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Select onValueChange={(list_id) => { onAction({ contact_ids: contactIds, action: "add_to_list", list_id }); setShowList(false); }}>
            <SelectTrigger className="rounded-sm bg-secondary/40 h-8 w-44" data-testid="bulk-list-select"><SelectValue placeholder="Choose list…" /></SelectTrigger>
            <SelectContent>
              {lists.length === 0 && <div className="p-2 text-xs text-muted-foreground">No lists — create one first</div>}
              {lists.map((L) => <SelectItem key={L.id} value={L.id}>{L.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setShowList(false)}><X className="h-3.5 w-3.5" /></Button>
        </div>
      )}
    </div>
  );
}

function NewListDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", color: "#25D366" });
  const mut = useMutation({
    mutationFn: () => api.post("/contact-lists", form),
    onSuccess: () => { toast.success("List created"); onCreated(); onClose(); },
    onError: () => toast.error("Couldn't create list"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader><DialogTitle>New Contact List</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="new-list-name" className="mt-1.5 rounded-sm bg-secondary/40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></div>
          <div><Label>Description</Label><Input className="mt-1.5 rounded-sm bg-secondary/40" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div>
            <Label>Color</Label>
            <div className="mt-1.5 flex gap-2 items-center">
              {["#25D366", "#1877F2", "#E1306C", "#FBBF24", "#38BDF8", "#A78BFA", "#F87171"].map((c) => (
                <button key={c} onClick={() => setForm({ ...form, color: c })} className={`h-6 w-6 rounded-full border-2 transition-transform ${form.color === c ? "border-white scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="new-list-submit" className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={!form.name || mut.isPending}>Create list</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContactDetailDialog({ contactId, lists, onClose, isAdmin }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const { data: contact, isLoading } = useQuery({
    queryKey: ["contact", contactId],
    queryFn: () => api.get(`/contacts/${contactId}`).then((r) => r.data),
  });
  React.useEffect(() => {
    if (contact) {
      setForm({
        name: contact.name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        whatsapp_phone: contact.whatsapp_phone || "",
        company_name: contact.company_name || "",
        notes: contact.notes || "",
        tags: (contact.tags || []).join(", "),
      });
    }
  }, [contact]);

  const patchMut = useMutation({
    mutationFn: () => api.patch(`/contacts/${contactId}`, { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) }),
    onSuccess: () => { toast.success("Updated"); setEditing(false); qc.invalidateQueries({ queryKey: ["contact", contactId] }); qc.invalidateQueries({ queryKey: ["contacts"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/contacts/${contactId}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["contacts"] }); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });
  const listMut = useMutation({
    mutationFn: (body) => api.post("/contacts/bulk", { contact_ids: [contactId], ...body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contact", contactId] }); qc.invalidateQueries({ queryKey: ["contact-lists"] }); },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
        {isLoading || !contact ? (<div className="text-sm text-muted-foreground">Loading…</div>) : (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">Contact — {contact.name}</DialogTitle>
              <div className="flex items-start gap-4">
                <div className="relative flex-none">
                  <ContactAvatar src={contact.avatar_url} name={contact.name} size={64} />
                  <span className="absolute -bottom-1 -right-1"><ChannelIcon channel={contact.source_channel} size={12} badge /></span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-2xl font-extrabold">{contact.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">Source: {contact.source_channel}</div>
                  {(contact.list_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(contact.list_ids || []).map((lid) => {
                        const L = lists.find((x) => x.id === lid);
                        if (!L) return null;
                        return (
                          <Badge key={lid} className="rounded-sm text-[10px] group cursor-pointer hover:bg-red-500/20 hover:text-red-300" style={{ backgroundColor: L.color + "22", borderColor: L.color + "66", color: L.color }} onClick={() => listMut.mutate({ action: "remove_from_list", list_id: lid })}>
                            {L.name} <X className="h-2.5 w-2.5 ml-1 inline opacity-0 group-hover:opacity-100" />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {!editing && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(true)} data-testid="contact-edit-btn"><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>}
                    {editing && (<>
                      <Button size="sm" className="rounded-sm bg-primary" onClick={() => patchMut.mutate()} disabled={patchMut.isPending} data-testid="contact-save-btn"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(false)} data-testid="contact-cancel-btn"><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
                    </>)}
                    <Select onValueChange={(list_id) => listMut.mutate({ action: "add_to_list", list_id })}>
                      <SelectTrigger className="h-8 rounded-sm text-xs w-40 bg-secondary/40" data-testid="contact-add-to-list"><SelectValue placeholder="Add to list…" /></SelectTrigger>
                      <SelectContent>
                        {lists.length === 0 && <div className="p-2 text-xs text-muted-foreground">No lists yet</div>}
                        {lists.filter((L) => !(contact.list_ids || []).includes(L.id)).map((L) => <SelectItem key={L.id} value={L.id}>{L.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {isAdmin && (
                      <Button size="sm" variant="outline" className="rounded-sm border-red-500/30 text-red-400 hover:text-red-300" onClick={() => { if (window.confirm("Delete this contact? This is permanent.")) deleteMut.mutate(); }} data-testid="contact-delete-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </div>
              </div>
            </DialogHeader>
            <Tabs defaultValue="profile" className="mt-4">
              <TabsList className="rounded-sm bg-secondary/40">
                <TabsTrigger value="profile" className="rounded-sm">Profile</TabsTrigger>
                <TabsTrigger value="conversations" className="rounded-sm">Conversations · {contact.conversations?.length || 0}</TabsTrigger>
                <TabsTrigger value="deals" className="rounded-sm">Deals · {contact.deals?.length || 0}</TabsTrigger>
                <TabsTrigger value="leads" className="rounded-sm">Leads · {contact.leads?.length || 0}</TabsTrigger>
                <TabsTrigger value="statement" className="rounded-sm" data-testid="contact-statement-tab">Statement</TabsTrigger>
              </TabsList>
              <TabsContent value="profile" className="mt-4 space-y-3">
                <ProfileField label="Name" editing={editing} value={editing ? form?.name : contact.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
                <ProfileField label="Email" editing={editing} value={editing ? form?.email : contact.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
                <ProfileField label="Phone" editing={editing} value={editing ? form?.phone : contact.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                <ProfileField label="WhatsApp phone" editing={editing} value={editing ? form?.whatsapp_phone : contact.whatsapp_phone} onChange={(v) => setForm((f) => ({ ...f, whatsapp_phone: v }))} />
                <ProfileField label="Company" editing={editing} value={editing ? form?.company_name : contact.company_name} onChange={(v) => setForm((f) => ({ ...f, company_name: v }))} />
                <ProfileField label="Tags (comma-separated)" editing={editing} value={editing ? form?.tags : (contact.tags || []).join(", ")} onChange={(v) => setForm((f) => ({ ...f, tags: v }))} />
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Notes</Label>
                  {editing ? (<Textarea value={form?.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="rounded-sm mt-1.5 bg-secondary/40" rows={4} />)
                    : (<div className="mt-1.5 text-sm whitespace-pre-wrap min-h-[40px]">{contact.notes || <span className="text-muted-foreground">No notes yet.</span>}</div>)}
                </div>
              </TabsContent>
              <TabsContent value="conversations" className="mt-4 space-y-2">
                {(contact.conversations || []).length === 0 && <div className="text-sm text-muted-foreground">No conversations yet.</div>}
                {(contact.conversations || []).map((c) => (
                  <Link key={c.id} to={`/inbox?conversation=${c.id}`} onClick={onClose} className="block bg-secondary/40 border border-border rounded-sm p-3 hover:border-primary/60 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 text-xs">
                        <ChannelIcon channel={c.channel} size={11} badge />
                        <span className="capitalize font-semibold" style={{ color: (CHANNEL_COLORS[c.channel] || CHANNEL_COLORS.chat).color }}>{c.channel}</span>
                        <Badge variant="outline" className="rounded-sm text-[10px] px-1.5 py-0">{c.status}</Badge>
                      </div>
                      <div className="text-[10px] text-muted-foreground">{relTime(c.last_message_at)}</div>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{c.last_message?.text || c.subject || "(no messages yet)"}</div>
                  </Link>
                ))}
              </TabsContent>
              <TabsContent value="deals" className="mt-4 space-y-2">
                {(contact.deals || []).length === 0 && <div className="text-sm text-muted-foreground">No deals linked yet.</div>}
                {(contact.deals || []).map((d) => (
                  <Link key={d.id} to={`/deals/${d.id}`} onClick={onClose} className="block bg-secondary/40 border border-border rounded-sm p-3 hover:border-primary/60 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-sm">{d.name}</div>
                      <div className="text-xs font-bold text-primary">{formatCurrency(d.value, d.currency)}</div>
                    </div>
                  </Link>
                ))}
              </TabsContent>
              <TabsContent value="leads" className="mt-4 space-y-2">
                {(contact.leads || []).length === 0 && <div className="text-sm text-muted-foreground">No leads yet.</div>}
                {(contact.leads || []).map((l) => (
                  <div key={l.id} className="bg-secondary/40 border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm"><span className="capitalize font-semibold" style={{ color: (CHANNEL_COLORS[l.source_channel] || CHANNEL_COLORS.chat).color }}>{l.source_channel}</span> · <span className="capitalize">{l.status}</span></div>
                      <div className="text-[10px] text-muted-foreground">{relTime(l.created_at)}</div>
                    </div>
                  </div>
                ))}
              </TabsContent>
              <TabsContent value="statement" className="mt-4">
                <StatementTab contactId={contactId} contact={contact} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileField({ label, value, editing, onChange }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
      {editing ? (<Input className="rounded-sm mt-1.5 bg-secondary/40" value={value || ""} onChange={(e) => onChange(e.target.value)} />)
        : (<div className="mt-1.5 text-sm min-h-[24px]">{value || <span className="text-muted-foreground">—</span>}</div>)}
    </div>
  );
}

function NewContactDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company_name: "", notes: "" });
  const mut = useMutation({
    mutationFn: () => api.post("/contacts", { ...form, tags: [] }),
    onSuccess: (r) => { toast.success("Contact created"); onCreated(r.data.id); },
    onError: () => toast.error("Couldn't create contact"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="new-contact-name" className="mt-1.5 rounded-sm bg-secondary/40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="new-contact-email" className="mt-1.5 rounded-sm bg-secondary/40" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input data-testid="new-contact-phone" className="mt-1.5 rounded-sm bg-secondary/40" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div><Label>Company</Label><Input data-testid="new-contact-company" className="mt-1.5 rounded-sm bg-secondary/40" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea data-testid="new-contact-notes" className="mt-1.5 rounded-sm bg-secondary/40" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="new-contact-submit" className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={!form.name || mut.isPending}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementTab({ contactId, contact }) {
  const today = new Date().toISOString().slice(0, 10);
  const oneYearAgo = new Date(); oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const [fromDate, setFromDate] = useState(oneYearAgo.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(today);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["contact-statement", contactId, fromDate, toDate],
    queryFn: () => api.get(`/contacts/${contactId}/statement`, {
      params: { from_date: fromDate || undefined, to_date: toDate || undefined },
    }).then((r) => r.data),
  });

  const fmt = (n) => new Intl.NumberFormat(undefined, { style: "currency", currency: data?.currency || "SGD" }).format(n || 0);

  const emailMut = useMutation({
    mutationFn: () => api.post(`/contacts/${contactId}/statement/email`, {
      from_date: fromDate || null, to_date: toDate || null,
    }),
    onSuccess: (r) => toast.success(`Statement emailed to ${r.data.to}`),
    onError: (e) => toast.error(e.response?.data?.detail || "Email failed"),
  });

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/contacts/${contactId}/statement.pdf?from_date=${fromDate || ""}&to_date=${toDate || ""}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("leadify_token")}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement-${(contact.name || contactId).replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="contact-statement-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm mt-1 bg-secondary/40 h-9 w-40" data-testid="stmt-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm mt-1 bg-secondary/40 h-9 w-40" data-testid="stmt-to" />
        </div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => refetch()} disabled={isFetching} data-testid="stmt-refresh">
          {isFetching ? "Loading…" : "Refresh"}
        </Button>
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              const slug = contact.statement_public_slug;
              if (!slug) { toast.error("Public link not ready — refresh the contact"); return; }
              const url = `${window.location.origin}/statement/${slug}`;
              navigator.clipboard.writeText(url);
              toast.success("Public statement link copied");
            }}
            data-testid="stmt-copy-public-link"
          >
            <Link2 className="h-3.5 w-3.5 mr-1.5" />Copy public link
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={downloadPdf} data-testid="stmt-download-pdf">
            <Download className="h-3.5 w-3.5 mr-1.5" />Download PDF
          </Button>
          <Button
            size="sm"
            className="rounded-sm bg-primary"
            onClick={() => { if (!contact.email) { toast.error("Add an email on the contact first"); return; } emailMut.mutate(); }}
            disabled={emailMut.isPending}
            data-testid="stmt-email"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />{emailMut.isPending ? "Sending…" : "Email to customer"}
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="stmt-summary">
          <StmtTile label="Invoiced" value={fmt(data.totals.invoiced)} />
          <StmtTile label="Paid" value={fmt(data.totals.paid)} tone="green" />
          <StmtTile label="Balance" value={fmt(data.totals.balance)} tone={data.totals.balance > 0 ? "warn" : "green"} />
          <StmtTile label="Invoices" value={data.totals.invoice_count} />
        </div>
      )}

      {/* Ledger */}
      {data && (
        <div className="border border-border rounded-sm" data-testid="stmt-ledger">
          <div className="grid grid-cols-[100px_100px_1fr_100px_100px_120px] gap-2 px-3 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <div>Date</div><div>Ref</div><div>Description</div>
            <div className="text-right">Debit</div><div className="text-right">Credit</div><div className="text-right">Balance</div>
          </div>
          <div className="grid grid-cols-[100px_100px_1fr_100px_100px_120px] gap-2 px-3 py-2 border-b border-border text-xs italic text-muted-foreground">
            <div></div><div></div><div>Opening balance</div>
            <div></div><div></div><div className="text-right">{fmt(data.opening_balance)}</div>
          </div>
          {data.ledger.length === 0 && (
            <div className="px-3 py-8 text-center text-xs text-muted-foreground">No transactions in this range.</div>
          )}
          {data.ledger.map((row, i) => (
            <div key={i} className="grid grid-cols-[100px_100px_1fr_100px_100px_120px] gap-2 px-3 py-2 border-b border-border last:border-b-0 text-xs items-center">
              <div className="text-muted-foreground">{row.date || "—"}</div>
              <div className="font-mono text-[10px]">{row.ref}</div>
              <div className="truncate" title={row.description}>{row.description}</div>
              <div className={`text-right ${row.debit ? "text-foreground" : "text-muted-foreground/40"}`}>{row.debit ? fmt(row.debit) : "—"}</div>
              <div className={`text-right ${row.credit ? "text-green-400 font-semibold" : "text-muted-foreground/40"}`}>{row.credit ? fmt(row.credit) : "—"}</div>
              <div className="text-right font-semibold">{fmt(row.balance_after)}</div>
            </div>
          ))}
          <div className="grid grid-cols-[100px_100px_1fr_100px_100px_120px] gap-2 px-3 py-2 border-t-2 border-border bg-secondary/30 text-xs font-bold">
            <div></div><div></div><div>Closing balance</div>
            <div></div><div></div><div className="text-right">{fmt(data.closing_balance)}</div>
          </div>
        </div>
      )}

      {/* Aging */}
      {data && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-1.5">Outstanding aging</div>
          <div className="grid grid-cols-5 gap-2">
            <AgingCell label="Current" value={fmt(data.aging.current)} testId="aging-current" />
            <AgingCell label="1-30" value={fmt(data.aging.d1_30)} testId="aging-1-30" />
            <AgingCell label="31-60" value={fmt(data.aging.d31_60)} testId="aging-31-60" />
            <AgingCell label="61-90" value={fmt(data.aging.d61_90)} testId="aging-61-90" />
            <AgingCell label="90+" value={fmt(data.aging.d90_plus)} tone={data.aging.d90_plus > 0 ? "warn" : "neutral"} testId="aging-90-plus" />
          </div>
        </div>
      )}
    </div>
  );
}

function StmtTile({ label, value, tone = "neutral" }) {
  const cls = { neutral: "text-foreground", green: "text-green-400", warn: "text-amber-400" }[tone];
  return (
    <Card className="rounded-sm bg-card border-border p-3">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-display text-xl font-black mt-0.5 ${cls}`}>{value}</div>
    </Card>
  );
}

function AgingCell({ label, value, tone = "neutral", testId }) {
  const cls = { neutral: "text-foreground", warn: "text-amber-500 border-amber-500/50" }[tone];
  return (
    <div className={`border border-border rounded-sm px-2.5 py-2 ${cls}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
    </div>
  );
}

